import { call, all, put, takeLatest } from 'redux-saga/effects';

import history from '../../../services/history';
import notification from '../../../services/notification';
import teamService from '../../../services/modules/team/team-service';
import domainService from '../../../services/modules/domain/domain-service';
import tradingService from '../../../services/modules/trading/trading-service';
import productService from '../product/product-service';
import { ISA_ERROR } from '../../../constants';
import { GET_ALL_REQUEST, GET_BY_ID_REQUEST, SAVE_REQUEST, EDIT_ITEM, DELETE_ITEM } from './trading-constants';
import * as tradingActions from './trading-actions';
import { formatMessage } from '~/i18n/translate';

const INITIAL_VALUES = {
  selectedTeam: null,
  minimumMarginUser: 0,
  minimumMarginOrder: 0,
  minimumMarginItems: []
};

export function* getAll() {
  try {
    yield put(tradingActions.setLoadingData(true));

    const { data } = yield call(tradingService.getAllTrading);
    if (data) {
      yield put(tradingActions.getAllSuccess([...data]));
    }

    yield put(tradingActions.setLoadingData(false));
  } catch (error) {
    yield put(tradingActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(tradingActions.setLoadingData(true));

    const [
      { data: teams },
      { data: product },
      { data: brand },
      { data: category },
      { data: department },
      { data: provider },
      { data: section }
    ] = yield all([
      call(teamService.getTeams),
      call(productService.getProducts),
      call(domainService.getBrands),
      call(domainService.getCategories),
      call(domainService.getDepartments),
      call(domainService.getProviders),
      call(domainService.getSections)
    ]);

    const selects = {
      teams,
      brand,
      category,
      department,
      product,
      provider,
      section
    };

    if (!id) {
      yield put(
        tradingActions.getByIdSuccess({
          data: INITIAL_VALUES,
          selects
        })
      );
    } else {
      const { data } = yield call(tradingService.getTrading, id);
      if (data) {
        yield put(
          tradingActions.getByIdSuccess({
            data,
            selects
          })
        );
      }
    }
    yield put(tradingActions.setLoadingData(false));
  } catch (error) {
    yield put(tradingActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    const { id, payload } = data;
    yield put(tradingActions.setLoadingSend(true));

    const trading: any = {
      tradingType: 'team',
      responsibleID: payload?.selectedTeam?._id,
      minimumMarginOrder: payload?.minimumMarginOrder,
      minimumMarginUser: payload?.minimumMarginUser,
      minimumMarginItems: payload?.minimumMarginItems?.map((item: any) => {
        const getType = (type: string) => {
          switch (type) {
            case 'brand': {
              return 'brandExternalCode';
            }
            case 'department': {
              return 'departmentExternalCode';
            }
            case 'product': {
              return 'externalCode';
            }
            case 'provider': {
              return 'providerExternalCode';
            }
            case 'section': {
              return 'sectionExternalCode';
            }
            case 'category': {
              return 'categoryExternalCode';
            }
            default: {
              return 'brandExternalCode';
            }
          }
        };
        return {
          brandExternalCode: item.brand?.externalCode,
          departmentExternalCode: item.department?.externalCode,
          externalCode: item.product?.externalCode,
          providerExternalCode: item.provider?.externalCode,
          sectionExternalCode: item.section?.externalCode,
          categoryExternalCode: item.category?.externalCode,
          type: getType(item.type),
          value: parseFloat(item.value.toString().replace(',', '.').replace(' ', ''))
        };
      })
    };

    if (id) {
      yield call(tradingService.editTrading, { _id: id, ...trading });
    } else {
      yield call(tradingService.createTrading, trading);
    }
    yield call(history.push, `/engine/trading/`, {
      date: new Date().getTime()
    });
    yield put(tradingActions.setLoadingSend(false));
    yield call(
      notification.success,
      id ? formatMessage('tradingFormUpdatedSuccess') : formatMessage('tradingFormCreatedSuccess')
    );
  } catch (error) {
    yield put(tradingActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(tradingActions.setLoadingSend(true));
      yield call(tradingService.deleteTrading, id);
      yield put(tradingActions.setLoadingSend(false));
      yield put(tradingActions.getAllRequest());
      yield call(notification.success, formatMessage('tradingFormExcludeSuccess'));
    }
  } catch (error) {
    yield put(tradingActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/engine/trading/edit/${id}`);
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem)
]);
